import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { AiOutlineSearch } from "react-icons/ai";
import { DebounceInput } from "react-debounce-input";
import { RiEmotionSadLine } from "react-icons/ri";
import { parseAssetTypeInt } from "../../utils";
import { GiRocket } from "react-icons/gi";
import Skeleton from "react-loading-skeleton";
import find from "../../services/search-assets/find";
import useIcons from "../../hooks/useIcons";
import useGeneralContext from "../../context/GeneralContext";

const SearchBar = () => {
  const [searchResults, setSearchResults] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [hideDropDown, setHideDropDown] = useState(false);
  const { getURI } = useIcons();
  const { push } = useHistory();
  const { tourStart } = useGeneralContext();

  useEffect(() => {
    if (inputValue.length < 2) {
      setSearchResults(undefined);
      return false;
    }
    //if string only contains whitespaces.
    if (!inputValue.replace(/\s/g, "").length) {
      setSearchResults(undefined);
      return false;
    }
    setLoading(true);
    setSearchResults(undefined);

    (async () => {
      try {
        const getApiResults = await find(inputValue);
        if (getApiResults) {
          setSearchResults(getApiResults);
          setLoading(false);
        } else {
          setSearchResults(null);
          setLoading(false);
        }
      } catch (e) {
        setSearchResults(null);
        setLoading(false);
      }
    })();
  }, [inputValue]);

  useEffect(() => {
    if (tourStart) {
      setInputValue("");
    }
  }, [tourStart]);

  function getDestination(assetType, encodedId) {
    if (assetType === 4) {
      return `/unsupported-fiis`;
    }

    return `/analysis/${encodedId}`;
  }

  const hideSearchDropdown = () => {
    setHideDropDown(true);
    let unlockDropDown = setInterval(() => {
      setHideDropDown(false);
      clearInterval(unlockDropDown);
    }, 100);
  };

  const showValidResults = (data) => {
    if (data) {
      let requestStatus = data.requestStatus ?? false;
      var requestResults = data.results;
      if (requestStatus === 0) {
        return (
          <center>
            <span>
              <GiRocket size={48} />
            </span>
            <h4>Houston, temos um problema!</h4>
            <span>
              Infelizmente, os dados não aterrissaram da maneira correta.
              <br />
              Por favor, <b>tente novamente.</b>
            </span>
          </center>
        );
      } else if (requestStatus === 1) {
        return (
          <center>
            <span>
              <RiEmotionSadLine size={48} />
            </span>
            <h4>Nenhum ativo encontrado, por enquanto.</h4>
            <span>
              Todos os dias adicionamos milhares de ativos novos a plataforma,
              tente buscar novamente depois.
            </span>
          </center>
        );
      } else if (requestStatus === 2) {
        let resultItems = requestResults.map((item, i) => {
          return (
            <div
              className="media"
              key={i}
              onClick={() => {
                hideSearchDropdown();
                push(getDestination(item.type, item.id));
              }}
            >
              <img
                className="img-xs rounded-circle"
                alt="Pesquisar ativos"
                src={getURI(item.icon)}
              />
              <div className="media-body">
                <div>
                  <Link
                    to={getDestination(item.type, item.id)}
                    onClick={() => {
                      hideSearchDropdown();
                    }}
                  >
                    <strong> {item.name}</strong>{" "}
                  </Link>

                  <div className="badge badge-pill badge-secondary float-right">
                    {item.symbol}
                  </div>
                </div>

                <span>
                  {parseAssetTypeInt(item.type)}{" "}
                  {item.exchange ? "(" + item.exchange + ")" : false}
                </span>
              </div>
            </div>
          );
        });
        return <div className="az-notification-list">{resultItems}</div>;
      }
    }
  };

  const defaultResultsSuggestions = () => {
    const defaultSuggestions = defaultSuggestionsData.map((asset, i) => {
      return (
        <div
          className="media"
          key={i}
          onClick={() => {
            hideSearchDropdown();
            push(getDestination(asset.type, asset.id));
          }}
          id={i === 0 ? "first-suggestion" : undefined}
        >
          <img
            className="img-xs rounded-circle"
            alt="Pesquisar ativos"
            src={getURI(asset.icon)}
          />
          <div className="media-body">
            <div>
              <Link
                to={getDestination(asset.type, asset.id)}
                onClick={() => {
                  hideSearchDropdown();
                }}
              >
                <strong> {asset.name}</strong>{" "}
              </Link>
              <div className="badge badge-pill badge-secondary float-right">
                {asset.symbol}
              </div>
            </div>
            <span>
              {parseAssetTypeInt(asset.type)}{" "}
              {asset.exchange ? "(" + asset.exchange + ")" : false}
            </span>
          </div>
        </div>
      );
    });

    return (
      <div>
        <span className="az-notification-title">Sugestões</span>
        <p className="az-notification-text">Ativos mais buscados</p>
        <div className="az-notification-list">{defaultSuggestions}</div>
      </div>
    );
  };

  const resultList = () => {
    if (!loading && searchResults) {
      return showValidResults(searchResults);
    } else if (!searchResults && inputValue && searchResults !== null) {
      return loadingSkeleton();
    } else if (loading && searchResults !== null) {
      return loadingSkeleton();
    } else if (!inputValue) {
      return defaultResultsSuggestions();
    } else {
      return errorState();
    }
  };

  return (
    <div
      className={
        hideDropDown
          ? "dropdown az-header-notification closed"
          : "dropdown az-header-notification"
      }
    >
      <a href="#/" id="step-search-icon">
        <i>
          <AiOutlineSearch />
        </i>
      </a>
      <div className="dropdown-menu">
        <div className="az-dropdown-header mg-b-20 d-sm-none">
          <a href="#/" className="az-header-arrow">
            <i className="icon ion-md-arrow-back" />
          </a>
        </div>

        <div className="pos-relative">
          <div className="input-group-text" id="step-search-bar">
            <div className="d-flex pos-absolute t-8 l-25">
              <i className="icon ion-md-search"></i>
            </div>

            <DebounceInput
              value={inputValue}
              placeholder="Pesquise por ativos..."
              className="form-control pd-l-30"
              minLength={2}
              debounceTimeout={500}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </div>
        </div>

        <br />
        <div id="results_list">
          {inputValue ? resultList() : defaultResultsSuggestions()}
        </div>
      </div>
    </div>
  );
};

export const SearchBarMemo = React.memo(SearchBar);

const errorState = () => {
  return (
    <center>
      <span>
        <GiRocket size={48} />
      </span>
      <h4>Houston, temos um problema!</h4>
      <span>
        Infelizmente, os dados não aterrissaram da maneira correta.
        <br />
        Por favor, <b>tente novamente.</b>
      </span>
    </center>
  );
};

const loadingSkeleton = () => {
  var MediaCards = [];
  for (let i = 0; i < 7; i++) {
    MediaCards.push(
      <div className="media" key={i}>
        <Skeleton width={40} height={40} borderRadius={30} />
        <div className="media-body">
          <Skeleton style={{ marginTop: 10 }} height={12} width={"85%"} />
        </div>
      </div>
    );
  }
  return <div className="az-notification-list">{MediaCards}</div>;
};

const defaultSuggestionsData = [
  {
    name: "Petrobras Petroleo Brasileiro S.A",
    symbol: "PETR4",
    exchange: "BM&FBovespa",
    type: 1,
    id: "anorf",
    country_code: 32,
    icon: "PETR4.svg",
    currency_symbol: "BRL",
  },
  {
    name: "Ambev SA",
    symbol: "ABEV3",
    exchange: "BM&FBovespa",
    type: 1,
    id: "anr77",
    country_code: 32,
    icon: "ABEV3.svg",
    currency_symbol: "BRL",
  },
  {
    name: "Gerdau S.A",
    symbol: "GGBR4",
    exchange: "BM&FBovespa",
    type: 1,
    id: "ansn7",
    country_code: 32,
    icon: "GGBR4.svg",
    currency_symbol: "BRL",
  },
  {
    name: "Vale SA",
    symbol: "VALE3",
    exchange: "BM&FBovespa",
    type: 1,
    id: "annae",
    country_code: 32,
    icon: "VALE3.svg",
    currency_symbol: "BRL",
  },
  {
    name: "Bitcoin Real Brasileiro",
    symbol: "BTC/BRL",
    exchange: "MercadoBitcoin",
    type: 3,
    id: "aftenfo",
    country_code: 241,
    icon: "BTCBRL.svg",
    currency_symbol: "BRL",
  },
  {
    name: "Dólar Americano Real Brasileiro",
    symbol: "USD/BRL",
    exchange: "",
    type: 2,
    id: "tafh",
    country_code: 32,
    icon: "USDBRL.svg",
    currency_symbol: "BRL",
  },
  {
    name: "Ethereum Dólar Americano",
    symbol: "ETH/USD",
    exchange: "Binance",
    type: 3,
    id: "afrnaet",
    country_code: 249,
    icon: "ETHUSD.svg",
    currency_symbol: "USD",
  },
];
